import { BootstrapData } from 'elements-sdk'
import { Observable, Subject } from 'rxjs'
import { Logger } from 'common/log'
import { connect, Socket } from 'socket.io-client'
import { Injectable, Inject } from '@angular/core'
import { BOOTSTRAP_DATA } from 'api/providers'
import { PushEventMap } from 'api/push'

@Injectable({ providedIn: 'root' })
export class SocketIOFactory {
  create (): typeof Socket {
    return connect({
      rejectUnauthorized: false,
      timeout: 2000,
      reconnection: false,
      query: {
        subscriptions: true,
      },
    })
  }
}

@Injectable({ providedIn: 'root' })
export class SocketService {
  logger = new Logger(__filename)
  connectionLost = false
  reconnected$ = new Subject<void>()
  private subjects: Partial<Record<keyof PushEventMap, Subject<any>>> = {}
  private subjectObservables: Partial<Record<keyof PushEventMap, Observable<any>>> = {}
  private subscriptionCount = {}
  private offlineSubscriptions: string[] = []
  private socket: typeof Socket
  private disconnectedAt = Date.now()

  constructor (
    socketIOFactory: SocketIOFactory,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
  ) {
    this.socket = socketIOFactory.create()

    this.on('connect', () => {
      console.timeStamp('Socket.IO connect')
      this.send({ type: 'login' })
      for (const subject of this.offlineSubscriptions) {
        this.send({ type: 'subscribe', subject })
      }
      this.offlineSubscriptions = []
      if (this.connectionLost) {
        this.logger.info(`Connected after ${Math.floor((Date.now() - this.disconnectedAt) / 1000)}s`)
        this.reconnected$.next()
      }
      this.connectionLost = false
    })
    this.on('disconnect', () => {
      console.timeStamp('Socket.IO disconnect')
      this.connectionLost = true
      this.offlineSubscriptions = [...Object.keys(this.subjects)]
      this.disconnectedAt = Date.now()
      this.logger.debug('Disconnected')
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          this.socket.connect()
        }, 2000)
      }
    })
    this.on('error', e => {
      this.connectionLost = true
      this.logger.debug('Error', e)
    })

    this.on('message', (msg) => {
      if (msg[0] === '{') {
        msg = JSON.parse(msg)
      }
      this.logger.log('<<', msg)
      this.subjects[msg.subject]?.next(msg.data)
    })

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.socket.connect()
      }
    }, false)
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async send (msg: any): Promise<void> {
    this.logger.log('>>', msg)
    this.socket.emit(
      'message',
      {
        ...msg,
        sessionid: this.bootstrapData.sessionId,
      },
    )
  }

  isConnected (): boolean {
    return this.socket.connected
  }

  subject <K extends keyof PushEventMap> (subject: K): Observable<PushEventMap[K]> {
    if (!this.subjectObservables[subject]) {
      let refCount = 0
      this.subjectObservables[subject] = new Observable(observer => {
        if (refCount === 0) {
          this.subscribe(subject)
        }
        refCount++
        this.subjects[subject]?.subscribe(msg => observer.next(msg))
        return () => {
          refCount--
          if (refCount === 0) {
            this.unsubscribe(subject)
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.subjectObservables[subject]
          }
        }
      })
    }
    return this.subjectObservables[subject]!
  }

  private on (eventName, callback) {
    this.socket.on(eventName, (arg) => {
      callback.apply(this.socket, [arg])
    })
  }

  private subscribe (subject: keyof PushEventMap) {
    this.subjects[subject] ??= new Subject()
    this.subscriptionCount[subject] ??= 0
    this.subscriptionCount[subject]++

    if (!this.isConnected()) {
      this.offlineSubscriptions.push(subject)
    } else {
      this.send({ type: 'subscribe', subject })
    }
  }

  private unsubscribe (subject: keyof PushEventMap) {
    this.subscriptionCount[subject]--
    this.offlineSubscriptions = this.offlineSubscriptions.filter(x => x !== subject)
    if (!this.subscriptionCount[subject]) {
      this.subjects[subject]?.complete()
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.subjects[subject]
      this.send({ type: 'unsubscribe', subject })
    }
  }
}
